<template>
    <div id="recaptcha-invisible"
         class="recaptcha-invisible" />
</template>

<script>

import ServiceFactory from '@/services/service-factory'
import Store from '@/store'
import { ACTIONS } from '@/store/actions'

export default {

    /**
     * @returns {{rendered: boolean, widgetId: number, value: boolean, alreadyVerified: boolean}}
     */
    data() {
        return {
            alreadyVerified: false,
            value: false,
            widgetId: 0,
            rendered: false,
        }
    },

    computed: {

        /**
         * @returns {boolean}
         */
        enabled() {
            return !this.$config.recaptcha.disableOnLocal
        },

        /**
         * @returns {string}
         */
        sitekey() {
            return this.$config.recaptcha.invisibleKey
        },
    },

    async beforeMount() {
        await this._isVerifiedAlreadyAsync(this.enabled ? 'isVerifiedAsync' : 'bypassAsync')
    },

    mounted() {
        if (!this.alreadyVerified)
            this.render()
    },

    methods: {

        /**
         * Checks verification status.
         * @param {string} verifyCheckFunc A function name.
         * @returns {Promise<Boolean>}
         * @private
         */
        async _isVerifiedAlreadyAsync(verifyCheckFunc) {
            this.$logger.log('Is it Already Verified?', 'ReCAPTCHA')
            try {
                const service = ServiceFactory.captcha()
                const operationState = await service[verifyCheckFunc]()
                this.$logger.logObject(operationState, 'Verification response', 'ReCAPTCHA')
                this.alreadyVerified = operationState.error ? false : operationState.data.result
            } catch (err) {
                this.alreadyVerified = false
            } finally {
                this.value = this.alreadyVerified
                this.$emit('already-verified', this.alreadyVerified)
                this.$emit('verified', this.value)
            }

            return this.value
        },

        /**
         * Executes ReCAPTCHA.
         */
        execute() {
            this.$logger.log('Executing', 'ReCAPTCHA')
            // noinspection JSUnresolvedVariable
            if (!this.Externals.grecaptcha)
                this.$logger.warn('grecaptcha not loaded', 'ReCAPTCHA')

            if (!this.rendered) this.render()
            // noinspection JSUnresolvedVariable
            this.Externals.grecaptcha.execute(this.widgetId)
        },

        /**
         * Resets ReCAPTCHA.
         */
        reset() {
            this.$logger.log('Resetting', 'ReCAPTCHA')
            // noinspection JSUnresolvedVariable
            if (!this.Externals.grecaptcha)
                this.$logger.warn('grecaptcha not loaded', 'ReCAPTCHA')

            if (!this.rendered) this.render()
            // noinspection JSUnresolvedVariable
            this.Externals.grecaptcha.reset(this.widgetId)
            // noinspection JSUnresolvedVariable
            this.Externals.grecaptcha.reset()
        },

        /**
         * Renders the component.
         */
        render() {
            this.$logger.log('Initiating', 'ReCAPTCHA')
            // noinspection JSUnresolvedVariable
            if (this.Externals.grecaptcha && this.Externals.grecaptcha.render) {
                this.widgetId = this.Externals.grecaptcha.render('recaptcha-invisible', {
                    sitekey: this.sitekey,
                    size: 'invisible',
                    callback: 'recaptchaVerify',
                })
                this.Externals.recaptchaVerify = this.verifyAsync
                this.rendered = true
            } else {
                this.$logger.warn('Initialisation not available yet - will defer', 'ReCAPTCHA')
            }
        },

        /**
         * Verify the ReCAPTCHA.
         * @async
         * @param {string} response
         * @returns {Promise<Boolean>}
         */
        async verifyAsync(response) {
            this.$logger.log('Validating', 'ReCAPTCHA')
            if (!this.enabled) return true

            try {
                const service = ServiceFactory.captcha()
                const operationState = await service.verifyAsync(response)

                if (operationState.error) {
                    // noinspection ExceptionCaughtLocallyJS
                    throw new Error(operationState.error)
                }

                await Store.dispatch(ACTIONS.setRequestToken, operationState.data.token)

                this.value = operationState.data.result
            } catch (err) {
                this.value = false
                this.$logger.error(`Unable to verify input: ${ err.message }`, 'ReCAPTCHA')
            } finally {
                this.$emit('verified', this.value)
                this.reset()
            }

            return this.value
        },
    },
}
</script>

<style lang="scss">
// Ensure colours and other visual style elements are added to theme files, not placed in here.

.recaptcha-invisible {
    display: block;

    .grecaptcha-badge {
        left: -999px !important;
        position: absolute !important;

        @include bp-min-small {
            left: auto !important;
            bottom: 0.5rem !important;
            right: 0.5rem !important;
        }

        @include bp-min-mid {
            bottom: 1rem !important;
            right: 1rem !important;
        }
    }
}

</style>
